import { DownloadOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { useCallback } from 'react';

import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { integrationActionLog } from '@app/services/__global__/integrationActionLog';

interface ExportIntegrationLogButtonProps {
  /** Кнопку показываем не всем — правило в useIntegrationLogRecording. */
  isVisible: boolean;
}

/**
 * Кнопка «Выгрузить ошибки» (us/11024): отдаёт файл с историей действий в
 * реестре интеграций и ответами сервера, чтобы разбирать проблему по фактам,
 * а не по пересказу.
 */
export const ExportIntegrationLogButton = ({
  isVisible,
}: ExportIntegrationLogButtonProps) => {
  const { showNotification } = useNotification();
  const {
    params: { Database, UserId, ActiveBranchId },
  } = useUserPrint();

  const handleExport = useCallback(() => {
    const entries = integrationActionLog.getEntries();

    if (!entries.length) {
      showNotification(
        'Пока нечего выгружать',
        'info',
        'Выполните действие с интеграцией — перезапуск, запрос кода, изменение настроек — и попробуйте снова.'
      );

      return;
    }

    const report = {
      exportedAt: new Date().toISOString(),
      database: Database ?? null,
      userId: UserId ?? null,
      branchId: ActiveBranchId ?? null,
      userAgent: window.navigator.userAgent,
      appUrl: window.location.href,
      entriesCount: entries.length,
      entries,
    };

    // BOM в начале файла: без него редакторы открывают выгрузку в системной
    // кодировке, и русские названия филиалов превращаются в «кракозябры».
    const blob = new Blob(['\ufeff', JSON.stringify(report, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

    link.href = url;
    link.download = `integrations-log-${Database ?? 'crm'}-${
      ActiveBranchId ?? 'branch'
    }-${stamp}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [ActiveBranchId, Database, UserId, showNotification]);

  if (!isVisible) {
    return null;
  }

  return (
    <Button
      icon={<DownloadOutlined />}
      onClick={handleExport}
      data-testid="export-integration-log"
    >
      Выгрузить ошибки
    </Button>
  );
};
